import { useEffect, useRef, useState } from "react";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import type { OverlayScrollbarsComponentRef } from "overlayscrollbars-react";
import { VIRTUAL_CONVERSATION_ID } from "../constants";
import { useHelpWidgetContext } from "../context/HelpWidgetContext";
import { useAttachmentsUpload } from "../hooks/useAttachmentsUpload";
import { useConversationMutations } from "../hooks/useConversationMutations";
import { useConversationWebSocket } from "../hooks/useConversationWebSocket";
import { useMarkAsRead } from "../hooks/useMarkAsRead";
import { useShowConversation } from "../hooks/useShowConversation";
import { useRevealingMessageStore } from "../stores/useRevealingMessageStore";
import type { ConversationChannel } from "../types/ConversationChannel";
import type { ConversationStatus } from "../types/ConversationStatus";
import type { HelpWidgetMessage } from "../types/help_widget_message";
import { shouldShowBotProcessing } from "../utils/conversationViewHelpers";
import { ConversationViewAttachments } from "./ConversationViewAttachments";
import { ConversationViewFooter } from "./ConversationViewFooter";
import { ConversationViewLoadingStates } from "./ConversationViewLoadingStates";
import { ConversationViewSkeletonLoader } from "./ConversationViewSkeletonLoader";
import { LeaveQueueConfirmation } from "./LeaveQueueConfirmation";
import { HelpWidgetLayout } from "./common/HelpWidgetLayout";
import { Messages } from "./common/Messages";
import { ScrollToBottomButton } from "./common/ScrollToBottomButton";

export const ConversationView = ({
  conversationId,
}: {
  conversationId: string;
}) => {
  const {
    isShowingLeaveWarning,
    virtualMessages,
    virtualConversationType,
    clearVirtualMessages,
  } = useHelpWidgetContext();
  const isVirtualConversation = conversationId === VIRTUAL_CONVERSATION_ID;
  const { data: conversation, isLoading } = useShowConversation(conversationId);
  const [status, setStatus] = useState<ConversationStatus | null>(null);
  const [channel, setChannel] = useState<ConversationChannel | null>(null);
  const { addMessage, setMessages, visibleMessages } =
    useRevealingMessageStore();
  const [isBotProcessing, setIsBotProcessing] = useState(false);
  const [copilotLoadingText, setCopilotLoadingText] = useState<string | null>(
    null,
  );
  const [isWaitingForConfirmation, setIsWaitingForConfirmation] =
    useState(false);
  // Queue-related state
  const [isWaitingInQueue, setIsWaitingInQueue] = useState(false);
  const [queuePosition, setQueuePosition] = useState<number | null>(null);
  const [shouldShowScrollButton, setShouldShowScrollButton] = useState(false);

  const { markAsRead } = useMarkAsRead();

  useEffect(() => {
    if (isVirtualConversation) {
      setStatus("draft");
      setChannel("circle_bot");
      virtualMessages.forEach(message =>
        addMessage(message as HelpWidgetMessage),
      );
      setIsWaitingInQueue(false);
      setQueuePosition(null);
      setIsBotProcessing(
        shouldShowBotProcessing(virtualMessages as HelpWidgetMessage[]),
      );
    } else if (conversation) {
      setStatus(conversation.status);
      setChannel(conversation.channel || null);
      setMessages(conversation.messages);
      setIsBotProcessing(conversation.is_bot_processing || false);
      setCopilotLoadingText(conversation.copilot_action_loading_text || null);
      setIsWaitingForConfirmation(
        conversation.is_waiting_for_confirmation || false,
      );
      setIsWaitingInQueue(conversation.is_waiting_in_queue || false);
      setQueuePosition(conversation.queue_position || null);
    }
  }, [
    addMessage,
    conversation,
    isVirtualConversation,
    setMessages,
    virtualMessages,
  ]);

  useEffect(() => {
    if (!isVirtualConversation) {
      markAsRead({ conversationId });
    }
  }, [isVirtualConversation, conversationId, markAsRead]);

  const { uploadAttachments, attachments, cancelAttachment, setAttachments } =
    useAttachmentsUpload({ conversationId });

  const { createChatMessage, createChat, clickButton } =
    useConversationMutations({
      channel,
      clearVirtualMessages,
      conversationId,
      setAttachments,
      setIsBotProcessing,
    });

  useConversationWebSocket({
    conversationId,
    isVirtualConversation,
    setChannel,
    setStatus,
    setIsBotProcessing,
    setCopilotLoadingText,
    setIsWaitingForConfirmation,
    setIsWaitingInQueue,
    setQueuePosition,
    markAsRead,
  });

  const onActionButtonClick = (message: HelpWidgetMessage, event: string) => {
    clickButton({ conversationId, event, messageId: message.id });
  };

  const onVoteSuccess = (
    messageId: string,
    voteType: "upvote" | "downvote" | null,
  ) => {
    const updatedMessages = visibleMessages.map(msg =>
      msg.id === messageId ? { ...msg, vote: voteType } : msg,
    );
    setMessages(updatedMessages);
  };

  const osRef = useRef<OverlayScrollbarsComponentRef>(null);
  const isStickyRef = useRef(true);

  const scrollToBottom = () => {
    const instance = osRef.current?.osInstance();
    if (instance) {
      const { viewport } = instance.elements();
      viewport.scrollTo({ top: viewport.scrollHeight, behavior: "smooth" });
      isStickyRef.current = true;
    }
  };

  const onSendMessage = (text: string) => {
    const attachmentIds = attachments.map(
      attachment => attachment.attachmentId,
    ) as string[];

    if (isVirtualConversation) {
      createChat({
        text,
        virtual_conversation_type: virtualConversationType,
      });
    } else {
      createChatMessage({
        conversationId,
        text,
        attachment_ids: attachmentIds,
      });
    }

    // Force scroll to bottom and enable sticky mode when user sends a message
    isStickyRef.current = true;
    const instance = osRef.current?.osInstance();
    if (instance) {
      const { viewport } = instance.elements();
      viewport.scrollTo({ top: viewport.scrollHeight });
    }
  };

  useEffect(() => {
    const instance = osRef.current?.osInstance();
    if (!instance) return;

    const onScroll = () => {
      const { viewport } = instance.elements();
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      // Consider sticky if within 50px of bottom
      isStickyRef.current = distanceFromBottom < 50;
      setShouldShowScrollButton(distanceFromBottom > 100);
    };

    const onUpdated = () => {
      if (isStickyRef.current) {
        const { viewport } = instance.elements();
        viewport.scrollTo({ top: viewport.scrollHeight });
      }
    };

    // Initial scroll to bottom
    onUpdated();

    instance.on("scroll", onScroll);
    instance.on("updated", onUpdated);

    return () => {
      instance.off("scroll", onScroll);
      instance.off("updated", onUpdated);
    };
  }, [conversationId, isLoading]);

  // Reset sticky state and force scroll to bottom when conversation changes or finishes loading
  useEffect(() => {
    if (!isLoading) {
      isStickyRef.current = true;
      const instance = osRef.current?.osInstance();
      if (instance) {
        const { viewport } = instance.elements();
        viewport.scrollTo({ top: viewport.scrollHeight });
      }
    }
  }, [conversationId, isLoading]);

  if (isLoading && !isVirtualConversation) {
    return (
      <div className="flex size-full flex-1 flex-col">
        <ConversationViewSkeletonLoader />
      </div>
    );
  }
  if (isShowingLeaveWarning) {
    return <LeaveQueueConfirmation conversationId={conversationId} />;
  }

  return (
    <HelpWidgetLayout className="flex flex-1 flex-col overflow-hidden">
      <div className="relative flex flex-1 flex-col overflow-hidden">
        <OverlayScrollbarsComponent
          ref={osRef}
          className="flex flex-1 flex-col overflow-hidden"
          options={{
            scrollbars: {
              autoHide: "leave",
              autoHideDelay: 0,
            },
          }}
        >
          <div className="flex flex-col px-4">
            <Messages
              status={status}
              onActionButtonClick={onActionButtonClick}
              conversationId={conversationId}
              onVoteSuccess={onVoteSuccess}
              isBotProcessing={isBotProcessing}
            />
            <ConversationViewLoadingStates
              channel={channel}
              copilotLoadingText={copilotLoadingText}
              isBotProcessing={isBotProcessing}
              isWaitingForConfirmation={isWaitingForConfirmation}
              isWaitingInQueue={isWaitingInQueue}
              queuePosition={queuePosition}
            />
            <div className="my-4 flex flex-col gap-2">
              {status !== "closed" && (
                <ConversationViewAttachments
                  attachments={attachments}
                  cancelAttachment={cancelAttachment}
                />
              )}
            </div>
          </div>
        </OverlayScrollbarsComponent>
        {shouldShowScrollButton && (
          <ScrollToBottomButton onClick={scrollToBottom} />
        )}
      </div>
      <ConversationViewFooter
        attachments={attachments}
        channel={channel}
        isBotProcessing={isBotProcessing}
        onSendMessage={onSendMessage}
        status={status}
        sunshineConversationId={conversation?.sunshine_conversation_id}
        uploadAttachments={uploadAttachments}
      />
    </HelpWidgetLayout>
  );
};
